/**
 * Shared behavior for the Custom Options feature (Surprise me / Customize mode switch, pills
 * multi-select aggregation, and click-to-toggle tooltips).
 *
 * Loaded as a real <script src> asset (not inline per-render <script> tags in
 * snippets/custom-options-mode.liquid / snippets/custom-option-group.liquid) because the cart-edit
 * modal (sections/main-cart-items.liquid) builds its markup by fetching a product page and
 * transplanting DOM fragments out of it — <script> elements moved into the live document that way
 * (via appendChild/innerHTML on parsed-but-never-inserted nodes) never execute, per how browsers
 * handle script elements outside the initial parse. Loading this file directly on both the
 * product page and the cart page guarantees the behavior works regardless of where the markup
 * came from.
 */
(function () {
  if (window.__customOptionsInit) return;
  window.__customOptionsInit = true;

  function resetCustomOptionsGroup(group) {
    // Uncheck pills/toggle — but never the toggle's own hidden "off" sentinel input
    // (data-option-toggle-off): that one must keep the merchant-configured off value (e.g. "No"),
    // not be blanked, since it's what actually gets submitted once the visible checkbox is
    // unchecked.
    group.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(function (input) {
      input.checked = false;
    });
    // Multi-select pills also need their aggregate hidden input cleared directly — unchecking the
    // individual pill checkboxes above doesn't do this on its own (nothing re-runs the aggregation
    // listener just from setting .checked in JS, since that only fires on real user input events).
    group.querySelectorAll('[data-option-group-pills-aggregate]').forEach(function (aggregate) {
      aggregate.value = '';
    });
    group.querySelectorAll('textarea, input[type="text"]').forEach(function (input) {
      input.value = '';
    });
    // Sliders are NOT handled here — syncAllSliders() (called right after this, at every mode
    // switch, variant change, and initial page load) already blanks a slider's submitted property
    // whenever its group is hidden for any reason, mode included. Duplicating that here would just
    // be two sources of truth for the same rule.
  }

  document.addEventListener('click', function (event) {
    var modeButton = event.target.closest('.custom-options__mode-btn');
    if (modeButton) {
      var wrapper = modeButton.closest('[data-custom-options-scope]');
      if (wrapper) {
        var scopeId = wrapper.dataset.customOptionsScope;
        var isCustom = modeButton.dataset.mode === 'custom';

        wrapper.querySelectorAll('.custom-options__mode-btn').forEach(function (btn) {
          var active = btn === modeButton;
          btn.classList.toggle('active', active);
          btn.setAttribute('aria-pressed', active);
        });

        var modeInput = wrapper.querySelector('[data-custom-options-mode-input]');
        if (modeInput) {
          modeInput.value = isCustom ? modeInput.dataset.customValue : modeInput.dataset.surpriseValue;
        }

        document
          .querySelectorAll('[data-custom-options-group][data-custom-options-scope="' + scopeId + '"]')
          .forEach(function (group) {
            group.hidden = !isCustom;
            // hidden only hides the group visually — its inputs are still part of the form and
            // still get submitted. Without clearing them, switching from Custom (after picking
            // options) back to "Surprise me" left the old choices as line item properties even
            // though "Surprise me" was selected.
            if (!isCustom) resetCustomOptionsGroup(group);
          });
        // Sliders live inside these groups but aren't covered by resetCustomOptionsGroup — resync
        // them now that each group's hidden state (set just above) is current: blanks a slider's
        // property when its group just got hidden, or restores/recenters it when shown again.
        syncAllSliders();
      }
    }

    // Tooltips: click toggles open/closed (the primary interaction on touch devices, which have
    // no real hover). Desktop/mouse devices additionally get a hover reveal, handled purely in
    // CSS via the (hover: hover) and (pointer: fine) media feature.
    var tooltipTrigger = event.target.closest('.custom-options__tooltip');
    var openTooltip = document.querySelector('.custom-options__tooltip[aria-expanded="true"]');

    if (openTooltip && openTooltip !== tooltipTrigger) {
      openTooltip.setAttribute('aria-expanded', 'false');
    }

    if (tooltipTrigger) {
      var isOpen = tooltipTrigger.getAttribute('aria-expanded') === 'true';
      tooltipTrigger.setAttribute('aria-expanded', String(!isOpen));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openTooltip = document.querySelector('.custom-options__tooltip[aria-expanded="true"]');
    if (openTooltip) openTooltip.setAttribute('aria-expanded', 'false');
  });

  document.addEventListener('change', function (event) {
    var checkbox = event.target.closest('[data-option-group-pills-item]');
    if (!checkbox) return;

    var groupId = checkbox.dataset.optionGroupPillsItem;
    var aggregate = document.querySelector('[data-option-group-pills-aggregate="' + groupId + '"]');
    if (!aggregate) return;

    var checked = document.querySelectorAll('[data-option-group-pills-item="' + groupId + '"]:checked');
    aggregate.value = Array.prototype.map
      .call(checked, function (el) {
        return el.value;
      })
      .join(', ');
  });

  // --- Slider group ------------------------------------------------------------------------
  // A generic range-input group (see snippets/custom-option-group.liquid, group_type 'slider').
  // Two independent, opt-in behaviors, both driven purely by data-attributes so the same markup
  // serves both a plain single-value scale (e.g. an acidity/bitterness/sweetness 1-3 slider — no
  // labels configured, no variant ties) and a two-sided split slider tied to a variant, such as a
  // "how many of your N bags should be Filter vs Espresso" slider that only appears once the
  // shopper's Brew type variant is set to "Mix".

  function formatSliderDisplay(sliderGroup, sliderInput) {
    var value = Number(sliderInput.value);
    var max = Number(sliderInput.max);
    var minLabel = sliderInput.dataset.sliderMinLabel;
    var maxLabel = sliderInput.dataset.sliderMaxLabel;
    var unitLabel = sliderInput.dataset.sliderUnitLabel;
    var unitSuffix = unitLabel ? ' ' + unitLabel : '';
    var minDisplay = sliderGroup.querySelector('[data-slider-min-display]');
    var maxDisplay = sliderGroup.querySelector('[data-slider-max-display]');
    var valueDisplay = sliderGroup.querySelector('[data-slider-value-display]');
    var hiddenInput = sliderGroup.querySelector('[data-slider-property]');
    var text;

    if (minDisplay && maxDisplay && minLabel && maxLabel) {
      // Both ends labeled: a two-sided split, e.g. "Filter 5/7 bags" / "Espresso 2/7 bags".
      var remainder = max - value;
      var minReading = value + '/' + max + unitSuffix;
      var maxReading = remainder + '/' + max + unitSuffix;
      minDisplay.textContent = minLabel + ' ' + minReading;
      maxDisplay.textContent = maxLabel + ' ' + maxReading;
      text = minLabel + ' ' + minReading + ', ' + maxLabel + ' ' + maxReading;
    } else if (minLabel && valueDisplay) {
      // Only the minimum end labeled: a single reading with context, e.g. "Filter 1/3 bags".
      text = minLabel + ' ' + value + '/' + max + unitSuffix;
      valueDisplay.textContent = text;
    } else {
      // No labels: a plain scale (e.g. an acidity/bitterness/sweetness 1-3 slider).
      text = value + unitSuffix;
      if (valueDisplay) valueDisplay.textContent = text;
    }

    if (hiddenInput) hiddenInput.value = text;
  }

  // Reads a variant option's currently selected value directly from the variant-picker inputs
  // (name="<Option name>-<position>", Dawn's own convention — see snippets/product-variant-options.liquid)
  // rather than from the resolved variant JSON, so it works identically on the product page (where
  // that JSON is only refreshed after an async section fetch completes) and in the cart-edit modal
  // (which never runs that fetch at all).
  //
  // Matched by POSITION (1st/2nd/3rd variant option), not by name: the option name Dawn renders
  // into that attribute is whatever the storefront's active language translates it to (e.g. "Brew
  // type" becomes "Bryggmetod" on a Swedish-language visit — product.options_with_values.name is
  // translated per-locale, same for the DOM input's name attribute), so a merchant-typed English
  // name like "Brew type" silently stops matching anything the moment the store has more than one
  // language. Position is a structural fact about the product (which option it is), never
  // translated, so it stays correct in every language.
  function getCurrentVariantOptionValue(position) {
    if (!position) return null;
    var suffix = '-' + position;
    // A plain `[name$="-1"]` attribute selector would be exact-match only, but Dawn's own
    // input_name capture (snippets/product-variant-options.liquid) renders this attribute with a
    // trailing newline (a pre-existing quirk, unrelated to position vs. name matching) — so match
    // loosely (name contains the suffix) and confirm with a trimmed comparison instead of relying
    // on the attribute being byte-exact.
    var checked = null;
    document.querySelectorAll('input[type="radio"][name*="' + suffix + '"]:checked').forEach(function (el) {
      if (!checked && el.name.trim().endsWith(suffix)) checked = el;
    });
    if (checked) return checked.value;
    var select = null;
    document.querySelectorAll('select[name*="' + suffix + '"]').forEach(function (el) {
      if (!select && el.name.trim().endsWith(suffix)) select = el;
    });
    return select ? select.value : null;
  }

  function syncSliderMax(sliderGroup, sliderInput) {
    var position = Number(sliderGroup.dataset.sliderMaxVariantOptionPosition);
    if (!position) return;
    var rawValue = getCurrentVariantOptionValue(position);
    if (rawValue == null) return;
    var match = /\d+/.exec(rawValue);
    if (!match) return;
    var newMax = parseInt(match[0], 10);
    if (newMax === Number(sliderInput.max)) return;
    sliderInput.max = newMax;
    // The bag count changed, so any previous split no longer means anything — recenter rather
    // than just clamp, landing on a middle value (never the extreme ends, even for an odd count).
    sliderInput.value = Math.floor(newMax / 2);
    formatSliderDisplay(sliderGroup, sliderInput);
  }

  function syncSliderVisibility(sliderGroup, sliderInput) {
    // A slider is only actually relevant when BOTH its own variant condition matches (if it has
    // one — e.g. Brew type must be Mix) AND its enclosing option_group isn't hidden by Surprise
    // me/Customize mode. Missing the mode half of this left a slider's default split (e.g. "Filter
    // 1/2 bags, Espresso 1/2 bags") silently submitted to the cart even while Surprise me was
    // selected, since resetCustomOptionsGroup never blanks sliders and nothing else ran at page
    // load (Surprise me is the default, un-clicked, mode).
    var outerGroup = sliderGroup.closest('[data-custom-options-group]');
    var modeHidden = !!(outerGroup && outerGroup.hidden);

    var position = Number(sliderGroup.dataset.sliderVisibleOptionPosition);
    var requiredValue = sliderGroup.dataset.sliderVisibleValue;
    var variantVisible = true;
    if (position && requiredValue) {
      variantVisible = getCurrentVariantOptionValue(position) === requiredValue;
    }

    var wasHidden = sliderGroup.hidden;
    var isVisible = variantVisible && !modeHidden;
    sliderGroup.hidden = !isVisible;

    if (!isVisible) {
      // Not relevant right now (wrong brew type, and/or Surprise me is selected): clear it rather
      // than silently submitting a stale or default split value.
      var hiddenInput = sliderGroup.querySelector('[data-slider-property]');
      if (hiddenInput) hiddenInput.value = '';
    } else if (wasHidden) {
      // Becoming relevant again. A slider tied to a variant-driven max (the bag-split case) starts
      // from a fresh centered position, since whatever it was last set to may have been for a
      // different bag count. A plain slider with no such tie (e.g. a generic 1-3 preference scale)
      // keeps its last position instead — the mode toggle alone shouldn't discard that choice — and
      // this branch then only needs to resync its just-blanked property from that position.
      if (sliderGroup.dataset.sliderMaxVariantOptionPosition) {
        sliderInput.value = Math.floor(Number(sliderInput.max) / 2);
      }
      formatSliderDisplay(sliderGroup, sliderInput);
    }
  }

  function syncAllSliders() {
    document.querySelectorAll('[data-slider-group]').forEach(function (sliderGroup) {
      var sliderInput = sliderGroup.querySelector('.custom-options__slider-input');
      if (!sliderInput) return;
      syncSliderMax(sliderGroup, sliderInput);
      syncSliderVisibility(sliderGroup, sliderInput);
    });
  }

  document.addEventListener('input', function (event) {
    var sliderInput = event.target.closest('.custom-options__slider-input');
    if (!sliderInput) return;
    var sliderGroup = sliderInput.closest('[data-slider-group]');
    if (sliderGroup) formatSliderDisplay(sliderGroup, sliderInput);
  });

  document.addEventListener('change', function (event) {
    if (event.target.matches('input[name], select[name]') && event.target.closest('variant-selects')) {
      syncAllSliders();
    }
  });

  // Initial pass: covers page load with a variant already selected (e.g. via ?variant= in the URL)
  // and the cart-edit modal's fetched markup, which never fires a variant-selects change event on
  // its own since prefillVariantSelects sets .checked directly rather than through user input.
  syncAllSliders();

  // --- Shared prefill helpers -------------------------------------------------------------
  // Used by the cart-edit modal (sections/main-cart-items.liquid) to restore a cart line's
  // variant/properties into a freshly-fetched product fragment, and by the product page itself
  // to restore the same state when arriving from a cart line's "edit on the product page" link
  // (see prefillFromURLProperties below). One implementation, two call sites, so they can't drift.

  function extractPropertyKey(name) {
    var match = /^properties\[(.+)\]$/.exec(name || '');
    return match ? match[1] : null;
  }

  function prefillVariantSelects(container, currentVariant) {
    var inputGroups = container.querySelectorAll('.product-form__input');
    inputGroups.forEach(function (group, index) {
      var currentValue = currentVariant ? currentVariant['option' + (index + 1)] : null;
      if (currentValue == null) return;

      group.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.checked = input.value === currentValue;
      });

      var select = group.querySelector('select');
      if (select) {
        Array.from(select.options).forEach(function (option) {
          option.selected = option.value === currentValue;
        });
      }
    });
  }

  function prefillCustomOptions(container, properties) {
    var modeInput = container.querySelector('[data-custom-options-mode-input]');
    if (modeInput) {
      var modeKey = extractPropertyKey(modeInput.name);
      var modeValue = modeKey ? properties[modeKey] : null;
      var isCustom = modeValue === modeInput.dataset.customValue;
      modeInput.value = isCustom ? modeInput.dataset.customValue : modeInput.dataset.surpriseValue;

      var scopeId = modeInput.dataset.customOptionsScope;
      container.querySelectorAll('.custom-options__mode-btn').forEach(function (btn) {
        var active = (btn.dataset.mode === 'custom') === isCustom;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', active);
      });
      container
        .querySelectorAll('[data-custom-options-group][data-custom-options-scope="' + scopeId + '"]')
        .forEach(function (group) {
          group.hidden = !isCustom;
        });
    }

    container.querySelectorAll('[data-option-group-pills-aggregate]').forEach(function (aggregate) {
      var key = extractPropertyKey(aggregate.name);
      var value = (key && properties[key]) || '';
      aggregate.value = value;
      var selected = value
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
      var groupId = aggregate.dataset.optionGroupPillsAggregate;
      container.querySelectorAll('[data-option-group-pills-item="' + groupId + '"]').forEach(function (input) {
        input.checked = selected.indexOf(input.value) !== -1;
      });
    });

    container.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if (
        el.hasAttribute('data-custom-options-mode-input') ||
        el.hasAttribute('data-option-group-pills-aggregate') ||
        el.hasAttribute('data-option-toggle-off')
      ) {
        // Fixed sentinel/aggregate inputs whose value must stay exactly what the template
        // rendered (e.g. the toggle's "off" value) — overwriting them from the current properties
        // here would corrupt that fixed value (see git history for the bug this caused).
        return;
      }
      var key = extractPropertyKey(el.name);
      if (!key || !(key in properties)) return;
      var value = properties[key];
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = el.value === value;
      } else {
        el.value = value;
      }
    });

    // Sliders: fix each slider's max/visibility from the now-current variant selection first (the
    // stored split, e.g. "Filter 5/7", is meaningless without the right max already in place), then
    // recover the range input's numeric position from that stored property string — the generic
    // loop above only restored the hidden input's raw text, not the visible <input type="range">.
    syncAllSliders();
    container.querySelectorAll('[data-slider-group]').forEach(function (sliderGroup) {
      var sliderInput = sliderGroup.querySelector('.custom-options__slider-input');
      var hiddenInput = sliderGroup.querySelector('[data-slider-property]');
      if (!sliderInput || !hiddenInput) return;
      var key = extractPropertyKey(hiddenInput.name);
      var storedValue = key ? properties[key] : null;
      if (storedValue == null) return;

      var minLabel = sliderInput.dataset.sliderMinLabel;
      var numericValue;
      if (minLabel) {
        var pattern = new RegExp('^' + minLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ' (\\d+)');
        var match = pattern.exec(storedValue);
        numericValue = match ? parseInt(match[1], 10) : NaN;
      } else {
        numericValue = parseInt(storedValue, 10);
      }
      if (isNaN(numericValue)) return;

      sliderInput.value = numericValue;
      formatSliderDisplay(sliderGroup, sliderInput);
    });
  }

  function normalizePropertiesForPrefill(raw) {
    // item.properties | json serializes as an array of [key, value] pairs, not a plain object.
    if (Array.isArray(raw)) {
      var obj = {};
      raw.forEach(function (pair) {
        obj[pair[0]] = pair[1];
      });
      return obj;
    }
    return raw || {};
  }

  window.CustomOptionsPrefill = {
    extractPropertyKey: extractPropertyKey,
    variantSelects: prefillVariantSelects,
    customOptions: prefillCustomOptions,
    normalizeProperties: normalizePropertiesForPrefill,
  };

  // --- Product page: restore properties from a cart line's "edit on the product page" link ----
  // A cart line's product link (sections/main-cart-items.liquid) carries its current line item
  // properties as ?properties[Key]=Value query params. On arrival, restore them into this
  // product's own custom-options form so a shopper editing "on the product page" sees what they
  // already chose instead of the form's blank defaults. Variant selection itself needs no extra
  // code: Shopify's ?variant= param is handled natively by the theme already.
  function propertiesFromSearchParams() {
    var params = new URLSearchParams(window.location.search);
    var properties = {};
    var found = false;
    params.forEach(function (value, key) {
      var match = /^properties\[(.+)\]$/.exec(key);
      if (match) {
        properties[match[1]] = value;
        found = true;
      }
    });
    return found ? properties : null;
  }

  function prefillFromURLProperties() {
    var properties = propertiesFromSearchParams();
    if (!properties) return;
    if (document.querySelector('[data-custom-options-mode-input], [data-custom-options-group]')) {
      prefillCustomOptions(document, properties);
    }
  }

  // This script is loaded with `defer`, so the DOM is already parsed by the time it runs — no
  // need to wait for DOMContentLoaded.
  prefillFromURLProperties();

  // --- Tier (Paket) tooltip ----------------------------------------------------------------
  // Adds an info tooltip next to the native Paket variant label explaining what Standard vs.
  // Premium actually gets you (e.g. "up to 30" vs. "all 120"). The coffee_finder block already
  // owns this copy/config (it needs the same tier settings for its own matching logic), so this
  // reads its config script directly rather than introducing a second settings surface.
  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function injectTierTooltip() {
    var configEl = document.querySelector('[data-coffee-finder-config]');
    if (!configEl) return;
    var config = JSON.parse(configEl.textContent);
    if (!config.tierTooltipText || !config.tierOptionPosition) return;

    // Loose contains-match + trimmed comparison rather than an exact `[name$="-3"]` attribute
    // selector — see the comment on getCurrentVariantOptionValue above for why (Dawn's own
    // input_name capture renders this attribute with a trailing newline).
    var suffix = '-' + config.tierOptionPosition;
    var input = null;
    document.querySelectorAll('input[name*="' + suffix + '"], select[name*="' + suffix + '"]').forEach(function (el) {
      if (!input && el.name.trim().endsWith(suffix)) input = el;
    });
    var wrapper = input ? input.closest('fieldset, .product-form__input') : null;
    var legend = wrapper ? wrapper.querySelector('legend, .form__label') : null;
    if (!legend || legend.querySelector('.custom-options__tooltip')) return;

    var text = escapeHtml(config.tierTooltipText);
    legend.innerHTML =
      '<span class="custom-options__legend-row">' +
      escapeHtml(legend.textContent.trim()) +
      '<button type="button" class="custom-options__tooltip" aria-expanded="false" aria-label="' +
      text +
      '"><span aria-hidden="true">i</span><span class="custom-options__tooltip-text" role="tooltip">' +
      text +
      '</span></button></span>';
  }

  injectTierTooltip();

  // Any variant change re-fetches and swaps the whole <variant-selects> markup (see
  // assets/product-info.js, updateOptionValues) — including the Paket legend our tooltip lives in —
  // well after this script's own synchronous 'change' listeners have already run. PUB_SUB_EVENTS
  // .variantChange fires once that swap is actually done, which is the only reliable point to
  // re-inject into the fresh legend (injectTierTooltip's own guard makes repeat calls harmless).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, injectTierTooltip);
  }

  // --- "What coffee can I expect?" finder -------------------------------------------------
  // Previews which real catalog coffees match the shopper's current Brew type / Paket (tier)
  // variant plus their Flavour Profile / Decaf choices. Matching goes through this store's real
  // collection filters (Shopify Search & Discovery: /collections/<handle>?filter.p.m...=<id>)
  // instead of re-implementing the flavour taxonomy in Liquid/JS — the "Flavour Profile" facet in
  // particular groups many underlying metaobject values into named categories (e.g. "Citrus &
  // Fresh"), and that grouping isn't exposed anywhere theme code can read it directly. Riding the
  // same filter engine the collection page itself uses means results are always correct.

  function readCoffeeFinderSelections(config) {
    // Deliberately does not read Flavour Profile/Process pill selections: the coffee-finder preview
    // only reflects Brew type, tier (Standard/Premium), and Decaf — those two pill groups are
    // preferences that don't narrow down which real coffees might show up.
    var selections = { brewFilterValues: [], isPremium: false, decafOn: false };

    var selectedVariantScript = document.querySelector('variant-selects [data-selected-variant]');
    var variant = selectedVariantScript ? JSON.parse(selectedVariantScript.textContent) : null;

    if (variant) {
      // Matched by POSITION (config.brewTypeOptionPosition/tierOptionPosition — e.g. 1 and 3), not
      // by comparing a translated option name against a merchant-typed one — see the comment on
      // getCurrentVariantOptionValue above for why name matching breaks under localization.
      if (config.brewTypeOptionPosition) {
        var brewValue = variant['option' + config.brewTypeOptionPosition];
        if (brewValue != null) {
          // Brew type restricts the pool for BOTH tiers alike — Mix/Omniroasted can be anything,
          // Filter gets Filter+Mix/Omniroasted, Espresso gets Espresso+Mix/Omniroasted. Only the
          // Standard-vs-Premium tier changes how many of that pool get shown (see standardTierLimit
          // below), not which brew types are eligible.
          var primaryValue = config.brewFilterValues[brewValue] || null;
          var mixValue = config.brewFilterValues.Mix || config.brewFilterValues.Omniroasted || null;
          if (primaryValue && primaryValue === mixValue) {
            // The shopper picked Mix/Omniroasted itself: their box can contain anything, so no
            // brew-type restriction at all — leave selections.brewFilterValues empty.
          } else {
            if (primaryValue) selections.brewFilterValues.push(primaryValue);
            if (mixValue) selections.brewFilterValues.push(mixValue);
          }
        }
      }
      if (config.tierOptionPosition) {
        var tierValue = variant['option' + config.tierOptionPosition];
        if (tierValue != null) selections.isPremium = tierValue === config.tierPremiumValue;
      }
    }

    document.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if (el.type !== 'checkbox' || !el.checked) return;
      if (extractPropertyKey(el.name) === config.decafPropertyName) selections.decafOn = true;
    });

    return selections;
  }

  // This store's filter.p.m.custom.espresso facet does NOT OR multiple values together when
  // they're passed as plain repeated params (`filter.x=a&filter.x=b`) — confirmed empirically:
  // only the LAST repeated value is honored (order-dependent), so a two-value brew pool (e.g.
  // Filter + Mix/Omniroasted) silently collapsed to just whichever value was listed last,
  // regardless of which brew type the shopper actually picked. The `[]` suffix on the param name
  // (`filter.x[]=a&filter.x[]=b`) is what this store's filter engine actually needs for a true
  // OR — verified to return the full union, order-independent. (An earlier version of this file
  // had it backwards; if this store's filter configuration ever changes again, re-verify with a
  // direct order-swap A/B test — see conversation history — rather than assuming either way.)
  function buildBrewParams(config, selections) {
    return selections.brewFilterValues.map(function (value) {
      return config.brewFilterParam + '[]=' + encodeURIComponent(value);
    });
  }

  function buildBrewQuery(config, selections) {
    var params = buildBrewParams(config, selections);
    params.push('sort_by=price-ascending');
    return params.join('&');
  }

  function buildCaffeineQuery(config, selections) {
    var params = buildBrewParams(config, selections);
    // Same multi-value facet quirk as buildBrewParams above (confirmed empirically for this
    // facet too — a two-value caffeine-content query without `[]` silently drops to just
    // whichever value came last), so this needs the same `[]` suffix.
    config.caffeineDecafValues.forEach(function (value) {
      params.push(config.caffeineFilterParam + '[]=' + encodeURIComponent(value));
    });
    params.push('sort_by=price-ascending');
    return params.join('&');
  }

  function fetchCoffeeFinderResults(config, query) {
    var collected = [];

    function fetchPage(page) {
      // Safety cap (12 pages) rather than trusting pagination markup to exist/parse a specific
      // way — an empty page is a reliable, markup-agnostic "no more results" signal on its own.
      if (page > 12) return Promise.resolve();

      // A section-rendering fetch (&section_id=product-grid) would be lighter, but that id is the
      // template-JSON block key, not the namespaced runtime section id Shopify's Section
      // Rendering API actually expects (same pitfall as the cart-edit modal's product fetch, see
      // sections/main-cart-items.liquid) — fetching the full collection page sidesteps needing to
      // know that runtime id at all.
      var url = '/collections/' + config.collectionHandle + '?' + query + '&page=' + page;
      return fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = Array.from(doc.querySelectorAll('.product-card-wrapper'));

          cards.forEach(function (card) {
            var tags = (card.dataset.productTags || '').split(',').map(function (t) {
              return t.trim();
            });
            if (tags.indexOf(config.excludeTag) === -1) collected.push(card);
          });

          if (cards.length === 0) return; // no more pages
          return fetchPage(page + 1);
        });
    }

    return fetchPage(1).then(function () {
      return collected;
    });
  }

  // Decaf/low-caffeine is deliberately not just another AND'd filter: with the toggle off those
  // coffees are excluded outright (a shopper who didn't ask for decaf shouldn't be shown any), and
  // with it on they're added into the pool alongside whatever the brew filter already matched
  // rather than narrowing that pool down to decaf-only.
  function combineWithCaffeineSet(brewCards, caffeineCards, decafOn) {
    var caffeineIds = {};
    caffeineCards.forEach(function (card) {
      caffeineIds[card.dataset.productId] = true;
    });

    if (!decafOn) {
      return brewCards.filter(function (card) {
        return !caffeineIds[card.dataset.productId];
      });
    }

    var seen = {};
    var merged = [];
    brewCards.concat(caffeineCards).forEach(function (card) {
      var id = card.dataset.productId;
      if (seen[id]) return;
      seen[id] = true;
      merged.push(card);
    });
    return merged;
  }

  function sortCardsByPrice(cards) {
    return cards.slice().sort(function (a, b) {
      return Number(a.dataset.productPrice) - Number(b.dataset.productPrice);
    });
  }

  function shuffle(array) {
    for (var i = array.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = array[i];
      array[i] = array[j];
      array[j] = tmp;
    }
    return array;
  }

  // Randomizes display order while keeping the same brand from landing back-to-back: group by
  // vendor, shuffle each group and the group order, then round-robin across groups. Only runs out
  // of options (and allows same-vendor adjacency) once every other vendor's cards are exhausted.
  function shuffleAvoidingAdjacentVendor(cards) {
    var groups = {};
    var vendorOrder = [];
    cards.forEach(function (card) {
      var vendor = card.dataset.productVendor || '';
      if (!groups[vendor]) {
        groups[vendor] = [];
        vendorOrder.push(vendor);
      }
      groups[vendor].push(card);
    });
    vendorOrder.forEach(function (vendor) {
      shuffle(groups[vendor]);
    });
    shuffle(vendorOrder);

    var result = [];
    var remaining = true;
    while (remaining) {
      remaining = false;
      vendorOrder.forEach(function (vendor) {
        var group = groups[vendor];
        if (group.length) {
          result.push(group.shift());
          if (group.length) remaining = true;
        }
      });
    }
    return result;
  }

  function arrangeForDisplay(cards) {
    var inStock = cards.filter(function (card) {
      return card.dataset.productAvailable !== 'false';
    });
    var soldOut = cards.filter(function (card) {
      return card.dataset.productAvailable === 'false';
    });
    return shuffleAvoidingAdjacentVendor(inStock).concat(shuffleAvoidingAdjacentVendor(soldOut));
  }

  function openCoffeeFinder() {
    var configEl = document.querySelector('[data-coffee-finder-config]');
    var resultsEl = document.getElementById('CoffeeFinderResults');
    var loadingEl = document.getElementById('CoffeeFinderLoading');
    var errorEl = document.getElementById('CoffeeFinderError');
    if (!configEl || !resultsEl || !loadingEl || !errorEl) return;

    var config = JSON.parse(configEl.textContent);

    errorEl.hidden = true;
    resultsEl.innerHTML = '';
    resultsEl.hidden = true;
    loadingEl.hidden = false;

    var selections = readCoffeeFinderSelections(config);
    var brewQuery = buildBrewQuery(config, selections);
    var caffeineQuery = buildCaffeineQuery(config, selections);

    Promise.all([
      fetchCoffeeFinderResults(config, brewQuery),
      fetchCoffeeFinderResults(config, caffeineQuery),
    ])
      .then(function (results) {
        var combined = combineWithCaffeineSet(results[0], results[1], selections.decafOn);
        var sorted = sortCardsByPrice(combined);
        var limited = selections.isPremium ? sorted : sorted.slice(0, config.standardTierLimit);
        var cards = arrangeForDisplay(limited);

        if (!cards.length) {
          errorEl.textContent = errorEl.dataset.noResults;
          errorEl.hidden = false;
          return;
        }
        cards.forEach(function (card) {
          resultsEl.appendChild(card);
        });
      })
      .catch(function () {
        errorEl.textContent = errorEl.dataset.error;
        errorEl.hidden = false;
      })
      .finally(function () {
        loadingEl.hidden = true;
        resultsEl.hidden = false;
      });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('.custom-options__coffee-finder-link')) openCoffeeFinder();
  });
})();
